import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Card, { 
    CardBody,
    CardHeader,
    CardLabel,
    CardTitle,
    CardActions
} from '../../components/bootstrap/Card';
import { selectLoginResponse } from '../../redux/login/login.selector';
import Button from '../../components/bootstrap/Button';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import Icon from '../../components/icon/Icon';
import TableSpinner from '../../common/table/TableSpinner';
import Input from '../../components/bootstrap/forms/Input';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import { getTransferHistoryService } from '../../redux/Reports/Reports.services';
import { getEmployeeLeaveReportStart } from '../../redux/NewReport/NewReport.actions';
import { selectEmployeeLeaveReportResponse } from '../../redux/NewReport/NewReport.selectors';
import { getAllEmployeeListStart, getAllEmployeeListResponseRestStart } from '../../redux/employeelist/employeelist.action';
import { selectAllEmployeeListData } from '../../redux/employeelist/employeelist.selector';
import { API_URL } from '../../redux/config';

interface Employee {
    empID: string;
    employeeID: number;
    employeeName: string;
    employeePhone: string;
    employeeGender: string;
    Designation: string;
    branchName: string;
    branchID: string;
    isManager?: number;
}

interface LeaveData {
    month: string;
    leaveCount: number;
    leaveDetails: string[];
    dates: string[];
    presentCount: number;
    absentDays: number;
    lateCheckIns: string[];
    earlyCheckOuts: string[];
    workingDays: number;
}

interface EmployeeLeaveReport {
    employee: Employee;
    leaveData: LeaveData[];
    totalLeaves: number;
}

interface PopupPosition {
    x: number;
    y: number;
}

interface PopupProps {
    details: string[] | string;
    onClose: () => void;
    position: PopupPosition;
    title: string;
}

interface PopupState {
    show: boolean;
    details: string[] | string;
    position: PopupPosition;
    title: string;
}

interface TransferHistory {
    transferID: string;
    transferDate: string;
    fromBranch: string;
    toBranch: string;
    transferType: string;
    toDate: string;
    isImmediateTransfer: string;
    isActive: string;
}

interface EmployeeDetails {
    employeeID: string;
    empID: string;
    employeeName: string;
    employeePhone: string;
    Designation: string;
    branchName: string;
    photoPath: string | null;
}

interface MonthlySummary {
    totalWorkingDays: number;
    presentDays: number;
    absentDays: number;
    leaveDays: number;
    lateCheckIns: number;
    earlyCheckOuts: number;
}

const DetailsPopup: React.FC<PopupProps> = ({ details, onClose, position, title }) => {
    const viewportWidth = window.innerWidth;
    const popupWidth = Math.min(350, viewportWidth - 20);
    
    const popupStyle: React.CSSProperties = {
        position: 'fixed',
        top: position.y,
        left: position.x,
        backgroundColor: 'white',
        padding: '12px',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        zIndex: 1000,
        maxWidth: `${popupWidth}px`,
        minWidth: '280px',
        width: 'auto',
        border: '1px solid #e9ecef',
        backdropFilter: 'blur(10px)',
        animation: 'fadeIn 0.2s ease-out',
        maxHeight: '300px',
        overflow: 'hidden'
    };

    const formatDates = (dates: string[]) => {
        return dates.map(date => {
            const d = new Date(date);
            return d.toLocaleDateString('en-GB', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric' 
            });
        });
    };

    const renderContent = () => {
        if (Array.isArray(details)) {
            if (title.includes('Dates')) {
                // Format dates for late check-ins and early checkouts
                const formattedDates = formatDates(details);
                return formattedDates.map((date, index) => (
                    <div key={index} style={{
                        marginBottom: '4px',
                        padding: '6px 8px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        borderLeft: '3px solid #007bff',
                        fontSize: '12px',
                        lineHeight: '1.3'
                    }}>
                        {date}
                    </div>
                ));
            } else {
                // Regular leave details
                return details.map((detail, index) => (
                    <div key={index} style={{
                        marginBottom: '4px',
                        padding: '6px 8px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        borderLeft: '3px solid #007bff',
                        fontSize: '12px',
                        lineHeight: '1.3'
                    }}>
                        {detail}
                    </div>
                ));
            }
        } else {
            return (
                <div style={{
                    padding: '6px 8px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    borderLeft: '3px solid #007bff',
                    fontSize: '12px',
                    lineHeight: '1.3'
                }}>
                    {details}
                </div>
            );
        }
    };

    return (
        <>
            <style>
                {`
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: translateY(-10px) scale(0.95);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0) scale(1);
                        }
                    }
                `}
            </style>
            <div style={popupStyle}>
                <div style={{ 
                    marginBottom: '8px', 
                    borderBottom: '1px solid #007bff', 
                    paddingBottom: '6px', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                }}>
                    <strong style={{ color: '#007bff', fontSize: '13px' }}>{title}</strong>
                    <Button 
                        color="primary"
                        onClick={onClose}
                        size="sm"
                        icon="Close"
                        className="btn-sm"
                        style={{ padding: '2px 6px', fontSize: '10px', color: '#007bff', backgroundColor: 'transparent', border: 'none' }}
                    />
                </div>
                <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    overflowX: 'hidden'
                }}>
                    {renderContent()}
                </div>
            </div>
            <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} />
        </>
    );
};

const MONTHS = [
    { value: '01', label: 'January', short: 'Jan' },
    { value: '02', label: 'February', short: 'Feb' },
    { value: '03', label: 'March', short: 'Mar' },
    { value: '04', label: 'April', short: 'Apr' },
    { value: '05', label: 'May', short: 'May' },
    { value: '06', label: 'June', short: 'Jun' },
    { value: '07', label: 'July', short: 'Jul' },
    { value: '08', label: 'August', short: 'Aug' },
    { value: '09', label: 'September', short: 'Sep' },
    { value: '10', label: 'October', short: 'Oct' },
    { value: '11', label: 'November', short: 'Nov' },
    { value: '12', label: 'December', short: 'Dec' }
];

const YEARS = [
    { value: '2025', label: '2025' },
    { value: '2026', label: '2026' }
];

const EmployeeLeaveReport = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loginData = useSelector(selectLoginResponse);
    const allEmployeeListData = useSelector(selectAllEmployeeListData);
    const employeeLeaveResp: any = useSelector(selectEmployeeLeaveReportResponse);
    
    const currentYear = new Date().getFullYear().toString();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState<EmployeeLeaveReport | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);

    const [popup, setPopup] = useState<PopupState>({
        show: false,
        details: [],
        position: { x: 0, y: 0 },
        title: ''
    });
    
    // State for employee details and transfer map
    const [employeeDetails, setEmployeeDetails] = useState<EmployeeDetails | null>(null);
    const [monthlySummary, setMonthlySummary] = useState<MonthlySummary | null>(null);
    const [transferHistory, setTransferHistory] = useState<TransferHistory[]>([]);
    const [isLoadingTransfer, setIsLoadingTransfer] = useState(false);
    const [monthlyDetailsLoaded, setMonthlyDetailsLoaded] = useState(false);
    const [showTransferMap, setShowTransferMap] = useState(false);
    const [showPayslipModal, setShowPayslipModal] = useState(false);
    const [isLoadingPayslip, setIsLoadingPayslip] = useState(false);
    const [showPayslipMonthSelector, setShowPayslipMonthSelector] = useState(false);

    // When encrypted response arrives, populate report + summary + details (single source of truth)
    useEffect(() => {
        if (!employeeLeaveResp?.data || !selectedEmployee) return;
        const data = employeeLeaveResp.data as EmployeeLeaveReport;
        const reportEmployeeId = data?.employee?.employeeID?.toString();
        const selectedId = selectedEmployee.employeeID.toString();
        if (reportEmployeeId && reportEmployeeId !== selectedId) return; // avoid applying stale response for another employee
        setReportData(data);
        setIsLoading(false);

        // Derive monthly summary from report data (so above/below stay in sync)
        const leaveData = data?.leaveData ?? [];
        const currentMonth = new Date().getMonth() + 1;
        const currentMonthStr = currentMonth.toString().padStart(2, '0');
        const currentMonthData = leaveData.find((d: LeaveData) => d.month === currentMonthStr);
        const monthDataToUse: LeaveData | undefined = currentMonthData ?? (leaveData.length > 0
            ? [...leaveData].sort((a, b) => parseInt(b.month, 10) - parseInt(a.month, 10))[0]
            : undefined);
        const summary: MonthlySummary = {
            totalWorkingDays: monthDataToUse?.workingDays ?? 0,
            presentDays: monthDataToUse?.presentCount ?? 0,
            absentDays: monthDataToUse?.absentDays ?? 0,
            leaveDays: monthDataToUse?.leaveCount ?? 0,
            lateCheckIns: monthDataToUse?.lateCheckIns?.length ?? 0,
            earlyCheckOuts: monthDataToUse?.earlyCheckOuts?.length ?? 0,
        };
        setMonthlySummary(summary);

        // Set employee details (photo fetched separately)
        const details: EmployeeDetails = {
            employeeID: selectedEmployee.employeeID.toString(),
            empID: selectedEmployee.empID,
            employeeName: selectedEmployee.employeeName,
            employeePhone: selectedEmployee.employeePhone,
            Designation: selectedEmployee.Designation,
            branchName: selectedEmployee.branchName,
            photoPath: null,
        };
        setEmployeeDetails(details);

        // Fetch transfer history for photo + transfer list (uses JWT via makeApiCall)
        const organisationID = loginData?.organisationID || loginData?.data?.organisationID || '1';
        getTransferHistoryService({
            employeeID: selectedEmployee.employeeID.toString(),
            organisationID,
        })
            .then((transferData: any) => {
                const rawPhoto = transferData?.employee?.employeePhoto || transferData?.employee?.photoPath;
                if (rawPhoto) {
                    const photoPath = String(rawPhoto).startsWith('http') ? rawPhoto : (API_URL.replace(/\/$/, '') + '/' + String(rawPhoto).replace(/^\//, ''));
                    setEmployeeDetails((prev) => prev ? { ...prev, photoPath } : null);
                }
                let transfers = Array.isArray(transferData?.transfers) ? transferData.transfers : (transferData?.transfers && typeof transferData.transfers === 'object' ? Object.values(transferData.transfers) : []);
                setTransferHistory([...transfers].reverse());
            })
            .catch(() => {
                setTransferHistory([]);
            });
    }, [employeeLeaveResp, selectedEmployee, loginData?.organisationID, loginData?.data?.organisationID]);

    // Fetch employees on component mount
    useEffect(() => {
        const organisationID = loginData?.organisationID || '1';
        dispatch(getAllEmployeeListStart(organisationID));
    }, [dispatch, loginData]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.position-relative')) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const filterEmployees = (employees: Employee[] | undefined, term: string) => {
        if (!employees || !term) return employees;
        term = term.toLowerCase();
        return employees.filter(
            (employee: Employee) =>
                employee.empID?.toLowerCase().includes(term) ||
                employee.employeeName?.toLowerCase().includes(term)
        );
    };

    const handleEmployeeSelect = (employee: Employee) => {
        setSelectedEmployee(employee);
        setSearchTerm(`${employee.empID} - ${employee.employeeName}`);
        setShowDropdown(false);
    };

    const handleGenerateReport = async () => {
        if (!selectedEmployee) {
            alert('Please select an employee');
            return;
        }

        let organisationID = null;
        
        if (loginData?.data?.organisationID) {
            organisationID = loginData.data.organisationID;
        } else if (loginData?.organisationID) {
            organisationID = loginData.organisationID;
        } else if (loginData?.payload?.organisationID) {
            organisationID = loginData.payload.organisationID;
        } else if (loginData?.payload?.data?.organisationID) {
            organisationID = loginData.payload.data.organisationID;
        }
        
        if (!organisationID) {
            console.error('Organization ID not found in login data');
            navigate('/auth-pages/login');
            return;
        }

        setIsLoading(true);
        setMonthlySummary(null);
        setEmployeeDetails(null);
        setTransferHistory([]);

        try {
            dispatch(getEmployeeLeaveReportStart({
                employeeID: selectedEmployee.employeeID.toString(),
                selectedYear: selectedYear,
                organisationID: organisationID
            }));
            // Summary, employee details and transfer history are set in useEffect when employeeLeaveResp.data arrives
        } catch (error: any) {
            console.error('Error generating report:', error);
            alert(error.message || 'Error generating report');
            setIsLoading(false);
        }
    };

    const handleCellClick = (event: React.MouseEvent, details: string[] | string, title: string) => {
        if (!details || (Array.isArray(details) && details.length === 0)) return;
        
        const rect = event.currentTarget.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const popupHeight = 250;
        const popupWidth = Math.min(350, viewportWidth - 20); // Responsive width
        
        // Calculate initial position - prioritize showing above the element
        let x = rect.left;
        let y = rect.top - popupHeight - 5; // Start with position above the element
        
        // Adjust horizontal position to keep popup within viewport
        if (x + popupWidth > viewportWidth) {
            x = viewportWidth - popupWidth - 10;
        }
        if (x < 10) {
            x = 10;
        }
        
        // If popup would go above the viewport, show it below the element
        if (y < 10) {
            y = rect.bottom + 5;
            
            // If still outside viewport, position at the bottom with scroll
            if (y + popupHeight > viewportHeight) {
                y = viewportHeight - popupHeight - 10;
            }
        }
        
        // Ensure minimum distance from top
        if (y < 10) {
            y = 10;
        }
        
        setPopup({
            show: true,
            details: details,
            position: { x, y },
            title: title
        });
    };

    // Add keyboard event listener for closing popup
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && popup.show) {
                setPopup({ show: false, details: [], position: { x: 0, y: 0 }, title: '' });
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [popup.show]);

    const handleExport = () => {
        if (!reportData) return;

        const headers = ['Month', 'Working Days', 'Present Days', 'Absent Days', 'Leave Count', 'Leave Dates'];
        const csvData = [
            headers.join(','),
            ...reportData.leaveData.map((monthData) => {
                const dates = monthData.dates.join('; ');
                return [
                    `"${MONTHS.find(m => m.value === monthData.month)?.label || monthData.month}"`,
                    `"${monthData.workingDays}"`,
                    `"${monthData.presentCount}"`,
                    `"${monthData.absentDays}"`,
                    `"${monthData.leaveCount}"`,
                    `"${dates}"`
                ].join(',');
            })
        ].join('\n');

        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `employee_leave_report_${reportData.employee.empID}_${selectedYear}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };
    


    const handleMonthlyDetails = () => {
        if (!employeeDetails) return;
        
        console.log('handleMonthlyDetails called for employee:', employeeDetails);
        setMonthlyDetailsLoaded(!monthlyDetailsLoaded); // Toggle the state
    };

    const handleTransferMap = async () => {
        if (!employeeDetails) {
            alert('Please select an employee first');
            return;
        }
        
        console.log('handleTransferMap called for employee:', employeeDetails);
        
        // If transfer history is not loaded, fetch it first
        if (!transferHistory || transferHistory.length === 0) {
            setIsLoadingTransfer(true);
            try {
                const organisationID = loginData?.organisationID || '1';
                const requestData = {
                    employeeID: employeeDetails.employeeID,
                    organisationID: organisationID
                };
                
                const transferData = await getTransferHistoryService(requestData);
                
                console.log('Transfer data received:', transferData);
                console.log('Transfers array:', transferData.transfers);
                
                let transfers = [];
                if (Array.isArray(transferData.transfers)) {
                    transfers = transferData.transfers;
                } else if (transferData.transfers && typeof transferData.transfers === 'object') {
                    transfers = Object.values(transferData.transfers);
                }
                
                console.log('Processed transfers:', transfers);
                // Reverse the order to show current branch at top
                const reversedTransfers = transfers.reverse();
                setTransferHistory(reversedTransfers);
                
                if (!transfers || transfers.length === 0) {
                    alert('No transfer history found for this employee.');
                    return;
                }
            } catch (error: any) {
                console.error('Error fetching transfer history:', error);
                alert(error.message || 'Error fetching transfer history');
                return;
            } finally {
                setIsLoadingTransfer(false);
            }
        }
        
        setShowTransferMap(!showTransferMap); // Toggle the state
    };

    const handlePayslip = () => {
        if (!employeeDetails) {
            alert('Please select an employee first');
            return;
        }
        
        setShowPayslipMonthSelector(true);
    };

    const handlePayslipForMonth = async (selectedMonth: string, selectedYear: number) => {
        setIsLoadingPayslip(true);
        try {
            // Use correct live path structure: /uploads/Organisation/1/{employeeID}/{Month}/Payslip.pdf
            const payslipPath = `uploads/Organisation/1/${employeeDetails!.employeeID}/${selectedMonth}/Payslip.pdf`;
            const fullPayslipUrl = API_URL + payslipPath;
            
            console.log('Opening payslip:', fullPayslipUrl);
            
            // Directly open the payslip in new tab without CORS check
            window.open(fullPayslipUrl, '_blank');
            setShowPayslipModal(true);
            setShowPayslipMonthSelector(false);
            
        } catch (error) {
            console.error('Error opening payslip:', error);
            alert('Error opening payslip. Please try again.');
        } finally {
            setIsLoadingPayslip(false);
        }
    };

    // Helper function to check if a month is in current month or previous 3 months
    const isRecentMonth = (monthValue: string) => {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1; // 1-12
        const currentMonthStr = currentMonth.toString().padStart(2, '0'); // '01'-'12'
        
        const monthNum = parseInt(monthValue, 10);
        const currentMonthNum = parseInt(currentMonthStr, 10);
        
        // Check if it's current month or any of the previous 3 months
        for (let i = 0; i <= 3; i++) {
            const checkMonth = ((currentMonthNum - i - 1 + 12) % 12) + 1;
            const checkMonthStr = checkMonth.toString().padStart(2, '0');
            if (monthValue === checkMonthStr) {
                return true;
            }
        }
        return false;
    };

    // Function to handle payslip click for a specific month
    const handlePayslipForMonthFromTable = async (monthValue: string) => {
        if (!employeeDetails) {
            alert('Please select an employee first');
            return;
        }
        
        const monthName = MONTHS.find(m => m.value === monthValue)?.label || monthValue;
        const currentYear = new Date().getFullYear();
        
       // await handlePayslipForMonth(monthName, currentYear);
    };

    return (
        <PageWrapper>
            <SubHeader className='mt-3'>
                <SubHeaderLeft>
                    <CardLabel icon='Person' iconColor='info'>
                        <CardTitle>Employee Leave Report</CardTitle>
                    </CardLabel>
                </SubHeaderLeft>
            </SubHeader>
            <Page container='fluid'>
                <Card stretch>
                    <CardHeader>
                        <CardLabel icon='FilterList'>
                            <CardTitle>Report Filters</CardTitle>
                        </CardLabel>
                        <CardActions>
                            {reportData && (
                                <Button
                                    color='success'
                                    size='sm'
                                    icon='Download'
                                    onClick={handleExport}>
                                    Export
                                </Button>
                            )}
                        </CardActions>
                    </CardHeader>
                    <div className='row h-100 pb-3'>
                        <div className='col-lg-12 col-md-12'>
                            <div className='row ps-4 pt-4'>
                                <div className='col-12'>
                                    <div className='row align-items-end'>
                                        <div className='col-md-3'>
                                            <div className='form-floating mb-3'>
                                                <select 
                                                    className='form-select'
                                                    id='yearSelect'
                                                    value={selectedYear}
                                                    onChange={(e) => setSelectedYear(e.target.value)}>
                                                    {YEARS.map(year => (
                                                        <option key={year.value} value={year.value}>
                                                            {year.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <label htmlFor='yearSelect'>Year</label>
                                            </div>
                                        </div>
                                        <div className='col-md-6'>
                                            <FormGroup
                                                id='empID'
                                                label='Employee Name'
                                                isFloating
                                                className='mb-3 position-relative'>
                                                <Input
                                                    type='text'
                                                    placeholder='Search by Employee Code or Name'
                                                    value={searchTerm}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setSearchTerm(e.target.value);
                                                        setShowDropdown(true);
                                                        if (!e.target.value) {
                                                            setSelectedEmployee(null);
                                                        }
                                                    }}
                                                    onFocus={() => setShowDropdown(true)}
                                                />
                                                <div 
                                                    className='position-absolute w-100 bg-white border rounded shadow-sm' 
                                                    style={{ 
                                                        top: '100%',
                                                        left: 0,
                                                        zIndex: 1000,
                                                        maxHeight: '200px',
                                                        overflowY: 'auto',
                                                        display: showDropdown ? 'block' : 'none'
                                                    }}>
                                                    {filterEmployees(allEmployeeListData?.data, searchTerm)
                                                        ?.filter(
                                                            (employee: Employee, index: number, self: Employee[]) =>
                                                                index === self.findIndex(
                                                                    (e: Employee) =>
                                                                        e.employeeID === employee.employeeID &&
                                                                        e.employeeName === employee.employeeName,
                                                                ),
                                                        )
                                                        .map((employee: Employee) => (
                                                            <div
                                                                key={`${employee.employeeID}-${employee.employeeName}`}
                                                                className='p-2 cursor-pointer hover:bg-light'
                                                                onClick={() => {
                                                                    handleEmployeeSelect(employee);
                                                                    setShowDropdown(false);
                                                                }}
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                role="button"
                                                                tabIndex={0}>
                                                                <strong>{employee.empID}</strong> - {employee.employeeName}
                                                                <br />
                                                                <small className='text-muted'>{employee.Designation}</small>
                                                            </div>
                                                        ))}
                                                    {(!filterEmployees(allEmployeeListData?.data, searchTerm) || 
                                                        filterEmployees(allEmployeeListData?.data, searchTerm)?.length === 0) && (
                                                        <div className='p-2 text-muted'>No employees found</div>
                                                    )}
                                                </div>
                                            </FormGroup>
                                        </div>
                                        <div className='col-md-3'>
                                            <div className='mb-3'>
                                                <Button
                                                    color='primary'
                                                    icon={!isLoading ? 'search' : ''}
                                                    onClick={handleGenerateReport}
                                                    isDisable={isLoading || !selectedEmployee}
                                                    className='w-100'
                                                    style={{
                                                        backgroundColor: '#007bff',
                                                        borderColor: '#007bff',
                                                        color: 'white'
                                                    }}>
                                                    {isLoading ? 'Generating...' : 'Generate Report'}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {isLoading && (
                                <div className='text-center my-5'>
                                    <TableSpinner />
                                </div>
                            )}

                            {!isLoading && reportData && (
                                <>
                                    <CardBody>
                                        
                                        {/* Employee Details Section */}
                                        {employeeDetails && monthlySummary && (
                                            <div className='row mb-3'>
                                            <div className='col-12'>
                                                    <Card>
                                                        <CardBody style={{ padding: '15px' }}>
                                                            <div className='row align-items-center'>
                                                                {/* Employee Photo */}
                                                                <div className='col-md-2 text-center'>
                                                                    <div style={{
                                                                        width: '120px',
                                                                        height: '120px',
                                                                        borderRadius: '50%',
                                                                        margin: '0 auto',
                                                    backgroundColor: '#f8f9fa',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        border: '3px solid #dee2e6',
                                                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                                                        overflow: 'hidden'
                                                                    }}>
                                                                        {employeeDetails.photoPath ? (
                                                                            <img 
                                                                                src={employeeDetails.photoPath} 
                                                                                alt="Employee Photo"
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    borderRadius: '50%',
                                                                                    objectFit: 'cover'
                                                                                }}
                                                                                onError={(e) => {
                                                                                    console.error('Image failed to load:', employeeDetails.photoPath);
                                                                                    e.currentTarget.style.display = 'none';
                                                                                }}
                                                                                onLoad={() => {
                                                                                    console.log('Image loaded successfully:', employeeDetails.photoPath);
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <div style={{
                                                                                width: '100%',
                                                                                height: '100%',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                backgroundColor: '#e9ecef',
                                                    color: '#6c757d',
                                                                                fontSize: '24px'
                                                                            }}>
                                                                                👤
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Employee Details */}
                                                                <div className='col-md-5'>
                                                                    <div style={{
                                                                        backgroundColor: '#f8f9fa',
                                                                        padding: '15px',
                                                                        borderRadius: '10px',
                                                                        border: '1px solid #e9ecef',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                                        height: '100%'
                                                                    }}>
                                                                        <h6 style={{ 
                                                                            color: '#495057', 
                                                                            marginBottom: '15px', 
                                                                            fontWeight: 'bold',
                                                                            borderBottom: '2px solid #dee2e6',
                                                                            paddingBottom: '8px'
                                                                        }}>
                                                                            📋 Employee Information
                                                                        </h6>
                                                                        <div className='row mb-2'>
                                                                            <div className='col-4'>
                                                                                <strong style={{ color: '#6c757d', fontSize: '13px' }}>Name:</strong>
                                                                            </div>
                                                                            <div className='col-8' style={{ fontWeight: '500', color: '#495057' }}>
                                                                                {employeeDetails.employeeName}
                                                                            </div>
                                                                        </div>
                                                                        <div className='row mb-2'>
                                                                            <div className='col-4'>
                                                                                <strong style={{ color: '#6c757d', fontSize: '13px' }}>Phone:</strong>
                                                                            </div>
                                                                            <div className='col-8' style={{ fontWeight: '500', color: '#495057' }}>
                                                                                {employeeDetails.employeePhone}
                                                                            </div>
                                                                        </div>
                                                                        <div className='row mb-2'>
                                                                            <div className='col-4'>
                                                                                <strong style={{ color: '#6c757d', fontSize: '13px' }}>Branch:</strong>
                                                                            </div>
                                                                            <div className='col-8' style={{ fontWeight: '500', color: '#495057' }}>
                                                                                {employeeDetails.branchName}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Monthly Summary */}
                                                                <div className='col-md-4'>
                                                                    <div style={{
                                                                        backgroundColor: '#f8f9fa',
                                                                        padding: '15px',
                                                                        borderRadius: '10px',
                                                                        border: '1px solid #e9ecef',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                                        height: '100%'
                                                                    }}>
                                                                        <h6 style={{ 
                                                                            color: '#495057', 
                                                                            marginBottom: '15px', 
                                                                            fontWeight: 'bold',
                                                                            borderBottom: '2px solid #dee2e6',
                                                                            paddingBottom: '8px'
                                                                        }}>
                                                                            📊 This Month Summary
                                                                        </h6>
                                                                        <div className='row mb-2'>
                                                                            <div className='col-6'>
                                                                                <strong style={{ color: '#6c757d', fontSize: '13px' }}>Working Days:</strong>
                                                                            </div>
                                                                            <div className='col-6' style={{ fontWeight: '500', color: '#495057' }}>
                                                                                {monthlySummary.totalWorkingDays}
                                                                            </div>
                                                                        </div>
                                                                        <div className='row mb-2'>
                                                                            <div className='col-6'>
                                                                                <strong style={{ color: '#6c757d', fontSize: '13px' }}>Present:</strong>
                                                                            </div>
                                                                            <div className='col-6' style={{ fontWeight: '500', color: '#495057' }}>
                                                                                {monthlySummary.presentDays}
                                                                            </div>
                                                                        </div>
                                                                        <div className='row mb-2'>
                                                                            <div className='col-6'>
                                                                                <strong style={{ color: '#6c757d', fontSize: '13px' }}>Absent:</strong>
                                                                            </div>
                                                                            <div className='col-6' style={{ fontWeight: '500', color: '#495057' }}>
                                                                                {monthlySummary.absentDays}
                                                                            </div>
                                                                        </div>
                                                                        <div className='row mb-2'>
                                                                            <div className='col-6'>
                                                                                <strong style={{ color: '#6c757d', fontSize: '13px' }}>Leave:</strong>
                                                                            </div>
                                                                            <div className='col-6' style={{ fontWeight: '500', color: '#495057' }}>
                                                                                {monthlySummary.leaveDays}
                                                                            </div>
                                                                        </div>
                                                                        <div className='row mb-2'>
                                                                            <div className='col-6'>
                                                                                <strong style={{ color: '#6c757d', fontSize: '13px' }}>Late Check-ins:</strong>
                                                                            </div>
                                                                            <div className='col-6' style={{ fontWeight: '500', color: '#495057' }}>
                                                                                {monthlySummary.lateCheckIns}
                                                                            </div>
                                                                        </div>
                                                                        <div className='row mb-2'>
                                                                            <div className='col-6'>
                                                                                <strong style={{ color: '#6c757d', fontSize: '13px' }}>Early Check-outs:</strong>
                                                                            </div>
                                                                            <div className='col-6' style={{ fontWeight: '500', color: '#495057' }}>
                                                                                {monthlySummary.earlyCheckOuts}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Transfer Map Button */}
                                                                <div className='col-md-1 d-flex flex-column align-items-center justify-content-center'>
                                                                    <Button
                                                                        color='warning'
                                                                        icon='Map'
                                                                        size='sm'
                                                                        onClick={handleTransferMap}
                                                                        title='View Transfer Map'
                                                                        className='mb-2'
                                                                        style={{ width: '40px', height: '40px', padding: '8px', borderRadius: '8px' }}
                                                                    >
                                                                    </Button>
                                                                    
                                                                    {/* Monthly Details Button */}
                                                                    <Button
                                                                        color='success'
                                                                        size='sm'
                                                                        onClick={handleMonthlyDetails}
                                                                        title='View Monthly Details'
                                                                        className='mb-2'
                                                                        style={{ width: '40px', height: '40px', padding: '8px', borderRadius: '8px' }}
                                                                    >
                                                                        📅
                                                                    </Button>
                                                                    

                                                                </div>
                                                            </div>
                                                        </CardBody>
                                                    </Card>
                                                </div>
                                            </div>
                                        )}
                                        

                                        

                                        
                                        {/* Transfer Map Popup */}
                                        {showTransferMap && transferHistory && transferHistory.length > 0 && (
                                            <div style={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                zIndex: 1050,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <div style={{
                                                    backgroundColor: 'white',
                                                    borderRadius: '12px',
                                                    padding: '30px',
                                                    maxWidth: '600px',
                                                    width: '90%',
                                                    maxHeight: '80vh',
                                                    overflowY: 'auto',
                                                    position: 'relative'
                                                }}>
                                                    {/* Close button */}
                                                    <button
                                                        onClick={() => setShowTransferMap(false)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '15px',
                                                            right: '20px',
                                                            background: 'none',
                                                            border: 'none',
                                                            fontSize: '24px',
                                                            cursor: 'pointer',
                                                            color: '#6c757d'
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                    
                                                    <h4 style={{ 
                                                        marginBottom: '25px', 
                                                        color: '#495057',
                                                        textAlign: 'center'
                                                    }}>
                                                        Transfer Journey Map
                                                    </h4>
                                                    
                                                    <div className='transfer-timeline' style={{
                                                        position: 'relative',
                                                        paddingLeft: '40px'
                                                    }}>
                                                        {/* Timeline line */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            left: '20px',
                                                            top: '0',
                                                            bottom: '0',
                                                            width: '3px',
                                                            backgroundColor: '#dee2e6'
                                                        }}></div>
                                                        
                                                        {transferHistory.map((transfer, index) => {
                                                            const isCurrentBranch = index === 0;
                                                            const isLastTransfer = index === transferHistory.length - 1;
                                                            
                                                            return (
                                                                <div key={transfer.transferID} style={{
                                                                    position: 'relative',
                                                                    marginBottom: '25px',
                                                                    padding: '20px',
                                                                    backgroundColor: isCurrentBranch ? '#f8fff9' : 'white',
                                                                    borderRadius: '10px',
                                                                    border: isCurrentBranch ? '2px solid #28a745' : '1px solid #dee2e6',
                                                                    boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
                                                                }}>
                                                                    {/* Timeline dot */}
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        left: '-30px',
                                                                        top: '25px',
                                                                        width: '16px',
                                                                        height: '16px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: isCurrentBranch ? '#28a745' : '#007bff',
                                                                        border: '4px solid white',
                                                                        boxShadow: '0 0 0 3px #dee2e6'
                                                                    }}></div>
                                                                    
                                                                    <div className='row align-items-center'>
                                                                        <div className='col-md-6'>
                                                                            <div style={{ 
                                                                                fontWeight: 'bold', 
                                                                                fontSize: '16px',
                                                                                color: isCurrentBranch ? '#28a745' : '#495057', 
                                                                                marginBottom: '5px' 
                                                                            }}>
                                                                                {new Date(transfer.transferDate).toLocaleDateString('en-GB', {
                                                                                    day: '2-digit',
                                                                                    month: '2-digit',
                                                                                    year: 'numeric'
                                                                                })}
                                                                            </div>
                                                                            <div style={{ fontSize: '13px', color: '#6c757d' }}>
                                                                                Transfer Date
                                                                            </div>
                                                                        </div>
                                                                        <div className='col-md-6'>
                                                                            <div style={{ 
                                                                                fontWeight: 'bold', 
                                                                                fontSize: '16px',
                                                                                color: isCurrentBranch ? '#28a745' : '#495057',
                                                                                marginBottom: '5px'
                                                                            }}>
                                                                                {transfer.toBranch}
                                                                            </div>
                                                                            <div style={{ fontSize: '13px', color: '#6c757d' }}>
                                                                                {isCurrentBranch ? 'Current Branch' : 'To Branch'}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    {/* Current branch indicator */}
                                                                    {isCurrentBranch && (
                                                                        <div style={{
                                                                            marginTop: '15px',
                                                                            padding: '10px 15px',
                                                                            backgroundColor: '#d4edda',
                                                                            color: '#155724',
                                                    borderRadius: '6px',
                                                                            fontSize: '14px',
                                                                            fontWeight: 'bold',
                                                                            textAlign: 'center'
                                                                        }}>
                                                                            🎯 Current Branch (Latest Transfer)
                                                    </div>
                                                                    )}
                                                                    
                                                                    {/* Downward arrow to previous transfer */}
                                                                    {!isLastTransfer && (
                                                                        <div style={{
                                                                            position: 'absolute',
                                                                            left: '50%',
                                                                            bottom: '-35px',
                                                                            transform: 'translateX(-50%)',
                                                                            fontSize: '24px',
                                                                            color: '#6c757d',
                                                                            fontWeight: 'bold'
                                                                        }}>
                                                                            ↓
                                                </div>
                                                                    )}
                                            </div>
                                                            );
                                                        })}
                                        </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Payslip Month Selector Modal */}
                                        {showPayslipMonthSelector && (
                                            <div style={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                zIndex: 1050,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <div style={{
                                                    backgroundColor: 'white',
                                                    borderRadius: '12px',
                                                    padding: '30px',
                                                    maxWidth: '400px',
                                                    width: '90%',
                                                    textAlign: 'center'
                                                }}>
                                                    <h4 style={{ 
                                                        marginBottom: '20px', 
                                                        color: '#495057'
                                                    }}>
                                                        📄 Select Payslip Month
                                                    </h4>
                                                    
                                                    <div style={{ marginBottom: '20px' }}>
                                                        <label style={{ 
                                                            display: 'block', 
                                                            marginBottom: '10px',
                                                            fontWeight: 'bold',
                                                            color: '#495057'
                                                        }}>
                                                            Month:
                                                        </label>
                                                        <select 
                                                            id="payslipMonth"
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px',
                                                                borderRadius: '6px',
                                                                border: '1px solid #dee2e6',
                                                                fontSize: '14px'
                                                            }}
                                                        >
                                                            {(() => {
                                                                const months = [
                                                                    'January', 'February', 'March', 'April', 'May', 'June',
                                                                    'July', 'August', 'September', 'October', 'November', 'December'
                                                                ];
                                                                const currentDate = new Date();
                                                                const currentMonthIndex = currentDate.getMonth();
                                                                
                                                                // Get current month and 3 previous months only
                                                                const recentMonths = [];
                                                                for (let i = 3; i >= 0; i--) {
                                                                    const monthIndex = (currentMonthIndex - i + 12) % 12;
                                                                    const monthName = months[monthIndex];
                                                                    const isCurrentMonth = i === 0;
                                                                    recentMonths.push(
                                                                        <option key={monthIndex} value={monthName} style={isCurrentMonth ? { fontWeight: 'bold' } : {}}>
                                                                            {monthName}
                                                                        </option>
                                                                    );
                                                                }
                                                                
                                                                return recentMonths;
                                                            })()}
                                                        </select>
                                                    </div>
                                                    
                                                    <div style={{ marginBottom: '25px' }}>
                                                        <label style={{ 
                                                            display: 'block', 
                                                            marginBottom: '10px',
                                                            fontWeight: 'bold',
                                                            color: '#495057'
                                                        }}>
                                                            Year:
                                                        </label>
                                                        <select 
                                                            id="payslipYear"
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px',
                                                                borderRadius: '6px',
                                                                border: '1px solid #dee2e6',
                                                                fontSize: '14px'
                                                            }}
                                                        >
                                                            {(() => {
                                                                const currentYear = new Date().getFullYear();
                                                                const years = [];
                                                                
                                                                // Show only current year
                                                                return [
                                                                    <option key={currentYear} value={currentYear} style={{ fontWeight: 'bold' }}>
                                                                        {currentYear}
                                                                    </option>
                                                                ];
                                                            })()}
                                                        </select>
                                                    </div>
                                                    
                                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                                        <button
                                                            onClick={() => setShowPayslipMonthSelector(false)}
                                                            style={{
                                                                background: '#6c757d',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                padding: '10px 20px',
                                                                fontSize: '14px',
                                                                cursor: 'pointer',
                                                                color: 'white'
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                const monthSelect = document.getElementById('payslipMonth') as HTMLSelectElement;
                                                                const yearSelect = document.getElementById('payslipYear') as HTMLSelectElement;
                                                                const selectedMonth = monthSelect.value;
                                                                const selectedYear = parseInt(yearSelect.value);
                                                                handlePayslipForMonth(selectedMonth, selectedYear);
                                                            }}
                                                            disabled={isLoadingPayslip}
                                                            style={{
                                                                background: '#007bff',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                padding: '10px 20px',
                                                                fontSize: '14px',
                                                                cursor: isLoadingPayslip ? 'not-allowed' : 'pointer',
                                                                color: 'white',
                                                                opacity: isLoadingPayslip ? 0.6 : 1
                                                            }}
                                                        >
                                                            {isLoadingPayslip ? 'Loading...' : 'Open Payslip'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Payslip Modal - Simplified for PDF opening */}
                                        {showPayslipModal && (
                                            <div style={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                zIndex: 1050,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <div style={{
                                                    backgroundColor: 'white',
                                                    borderRadius: '12px',
                                                    padding: '30px',
                                                    maxWidth: '400px',
                                                    width: '90%',
                                                    textAlign: 'center'
                                                }}>
                                                    <h4 style={{ 
                                                        marginBottom: '20px', 
                                                        color: '#495057'
                                                    }}>
                                                        📄 Payslip
                                                    </h4>
                                                    <p style={{ 
                                                        marginBottom: '25px', 
                                                        color: '#6c757d',
                                                        fontSize: '16px'
                                                    }}>
                                                        Your payslip has been opened in a new tab.
                                                    </p>
                                                    <button
                                                        onClick={() => setShowPayslipModal(false)}
                                                        style={{
                                                            background: '#007bff',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '10px 20px',
                                                            fontSize: '14px',
                                                            cursor: 'pointer',
                                                            color: 'white'
                                                        }}
                                                    >
                                                        Close
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Monthly Report Table - Only show when Calendar icon is clicked */}
                                        {monthlyDetailsLoaded && (
                                        <div className='table-responsive'>
                                            <table className='table table-modern table-hover' style={{ 
                                                fontSize: '13px', 
                                                width: '100%',
                                                borderCollapse: 'collapse',
                                                backgroundColor: 'white',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                        <th style={{
                                                            width: '8%',
                                                            padding: '10px 12px',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            backgroundColor: '#f8f9fa',
                                                            borderBottom: '2px solid #dee2e6',
                                                            borderRight: '1px solid #dee2e6',
                                                            color: '#495057',
                                                            textAlign: 'center'
                                                        }}>
                                                            S.No
                                                        </th>
                                                        <th style={{
                                                            width: '18%',
                                                            padding: '10px 12px',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            backgroundColor: '#f8f9fa',
                                                            borderBottom: '2px solid #dee2e6',
                                                            borderRight: '1px solid #dee2e6',
                                                            color: '#495057',
                                                            textAlign: 'left'
                                                        }}>
                                                            Month
                                                        </th>
                                                        <th style={{
                                                            width: '18%',
                                                            padding: '10px 12px',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            backgroundColor: '#f8f9fa',
                                                            borderBottom: '2px solid #dee2e6',
                                                            borderRight: '1px solid #dee2e6',
                                                            textAlign: 'center',
                                                            color: '#495057'
                                                        }}>
                                                            Leave Count
                                                        </th>
                                                        <th style={{
                                                            width: '15%',
                                                            padding: '10px 12px',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            backgroundColor: '#f8f9fa',
                                                            borderBottom: '2px solid #dee2e6',
                                                            borderRight: '1px solid #dee2e6',
                                                            textAlign: 'center',
                                                            color: '#495057'
                                                        }}>
                                                            Present Days
                                                        </th>
                                                        <th style={{
                                                            width: '15%',
                                                            padding: '10px 12px',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            backgroundColor: '#f8f9fa',
                                                            borderBottom: '2px solid #dee2e6',
                                                            borderRight: '1px solid #dee2e6',
                                                            textAlign: 'center',
                                                            color: '#495057'
                                                        }}>
                                                            Absent Days
                                                        </th>
                                                        <th style={{
                                                            width: '15%',
                                                            padding: '10px 12px',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            backgroundColor: '#f8f9fa',
                                                            borderBottom: '2px solid #dee2e6',
                                                            borderRight: '1px solid #dee2e6',
                                                            textAlign: 'center',
                                                            color: '#495057'
                                                        }}>
                                                            Late Check-ins
                                                        </th>
                                                        <th style={{
                                                            width: '15%',
                                                            padding: '10px 12px',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            backgroundColor: '#f8f9fa',
                                                            borderBottom: '2px solid #dee2e6',
                                                            borderRight: '1px solid #dee2e6',
                                                            textAlign: 'center',
                                                            color: '#495057'
                                                        }}>
                                                            Early Checkouts
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reportData.leaveData.map((monthData, index) => (
                                                        <tr key={index} style={{ 
                                                            borderBottom: '1px solid #dee2e6',
                                                            backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                                                        }}>
                                                            <td style={{
                                                                padding: '10px 12px',
                                                                fontSize: '13px',
                                                                fontWeight: '500',
                                                                verticalAlign: 'middle',
                                                                color: '#495057',
                                                                borderRight: '1px solid #dee2e6',
                                                                textAlign: 'center'
                                                            }}>
                                                                {index + 1}
                                                            </td>
                                                            <td style={{
                                                                padding: '10px 12px',
                                                                fontSize: '13px',
                                                                fontWeight: '500',
                                                                verticalAlign: 'middle',
                                                                color: '#495057',
                                                                borderRight: '1px solid #dee2e6'
                                                            }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                    <span>{MONTHS.find(m => m.value === monthData.month)?.label || monthData.month}</span>
                                                                    {/* {isRecentMonth(monthData.month) && (
                                                                        <button
                                                                            onClick={() => handlePayslipForMonthFromTable(monthData.month)}
                                                                            style={{
                                                                                background: '#007bff',
                                                                                border: 'none',
                                                                                cursor: 'pointer',
                                                                                fontSize: '14px',
                                                                                padding: '4px 6px',
                                                                                borderRadius: '4px',
                                                                                transition: 'all 0.2s ease',
                                                                                marginLeft: '8px',
                                                                                color: 'white',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                minWidth: '24px',
                                                                                height: '24px'
                                                                            }}
                                                                            onMouseEnter={(e) => {
                                                                                e.currentTarget.style.backgroundColor = '#0056b3';
                                                                                e.currentTarget.style.transform = 'scale(1.1)';
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.currentTarget.style.backgroundColor = '#007bff';
                                                                                e.currentTarget.style.transform = 'scale(1)';
                                                                            }}
                                                                            title={`View payslip for ${MONTHS.find(m => m.value === monthData.month)?.label || monthData.month}`}
                                                                        >
                                                                            📄
                                                                        </button>
                                                                    )} */}
                                                                </div>
                                                            </td>
                                                            <td style={{
                                                                textAlign: 'center',
                                                                padding: '10px 12px',
                                                                fontSize: '13px',
                                                                verticalAlign: 'middle',
                                                                borderRight: '1px solid #dee2e6'
                                                            }}>
                                                                {monthData.leaveCount > 0 ? (
                                                                    <span
                                                                        style={{
                                                                            display: 'inline-block',
                                                                            padding: '4px 8px',
                                                                            backgroundColor: '#007bff',
                                                                            color: 'white',
                                                                            borderRadius: '4px',
                                                                            cursor: 'pointer',
                                                                            fontWeight: '500',
                                                                            transition: 'all 0.2s ease',
                                                                            fontSize: '12px',
                                                                            minWidth: '25px'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#0056b3';
                                                                            e.currentTarget.style.transform = 'scale(1.05)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#007bff';
                                                                            e.currentTarget.style.transform = 'scale(1)';
                                                                        }}
                                                                        onClick={(e) => handleCellClick(e, monthData.leaveDetails, 'Leave Details')}
                                                                    >
                                                                        {monthData.leaveCount}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ 
                                                                        color: '#6c757d',
                                                                        fontWeight: '500'
                                                                    }}>0</span>
                                                                )}
                                                            </td>
                                                            <td style={{
                                                                textAlign: 'center',
                                                                padding: '10px 12px',
                                                                fontSize: '13px',
                                                                verticalAlign: 'middle',
                                                                borderRight: '1px solid #dee2e6'
                                                            }}>
                                                                <span style={{ 
                                                                    color: '#28a745',
                                                                    fontWeight: '500'
                                                                }}>{monthData.presentCount}</span>
                                                            </td>
                                                            <td style={{
                                                                textAlign: 'center',
                                                                padding: '10px 12px',
                                                                fontSize: '13px',
                                                                verticalAlign: 'middle',
                                                                borderRight: '1px solid #dee2e6'
                                                            }}>
                                                                <span style={{ 
                                                                    color: '#dc3545',
                                                                    fontWeight: '500'
                                                                }}>{monthData.absentDays}</span>
                                                            </td>
                                                            <td style={{
                                                                textAlign: 'center',
                                                                padding: '10px 12px',
                                                                fontSize: '13px',
                                                                verticalAlign: 'middle',
                                                                borderRight: '1px solid #dee2e6'
                                                            }}>
                                                                {monthData.lateCheckIns.length > 0 ? (
                                                                    <span
                                                                        style={{
                                                                            display: 'inline-block',
                                                                            padding: '4px 8px',
                                                                            backgroundColor: '#ffc107',
                                                                            color: '#212529',
                                                                            borderRadius: '4px',
                                                                            cursor: 'pointer',
                                                                            fontWeight: '500',
                                                                            transition: 'all 0.2s ease',
                                                                            fontSize: '12px',
                                                                            minWidth: '25px'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#e0a800';
                                                                            e.currentTarget.style.transform = 'scale(1.05)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#ffc107';
                                                                            e.currentTarget.style.transform = 'scale(1)';
                                                                        }}
                                                                        onClick={(e) => handleCellClick(e, monthData.lateCheckIns, 'Late Check-in Dates')}
                                                                    >
                                                                        {monthData.lateCheckIns.length}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ 
                                                                        color: '#6c757d',
                                                                        fontWeight: '500'
                                                                    }}>0</span>
                                                                )}
                                                            </td>
                                                            <td style={{
                                                                textAlign: 'center',
                                                                padding: '10px 12px',
                                                                fontSize: '13px',
                                                                verticalAlign: 'middle'
                                                            }}>
                                                                {monthData.earlyCheckOuts.length > 0 ? (
                                                                    <span
                                                                        style={{
                                                                            display: 'inline-block',
                                                                            padding: '4px 8px',
                                                                            backgroundColor: '#dc3545',
                                                                            color: 'white',
                                                                            borderRadius: '4px',
                                                                            cursor: 'pointer',
                                                                            fontWeight: '500',
                                                                            transition: 'all 0.2s ease',
                                                                            fontSize: '12px',
                                                                            minWidth: '25px'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#c82333';
                                                                            e.currentTarget.style.transform = 'scale(1.05)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#dc3545';
                                                                            e.currentTarget.style.transform = 'scale(1)';
                                                                        }}
                                                                        onClick={(e) => handleCellClick(e, monthData.earlyCheckOuts, 'Early Checkout Dates')}
                                                                    >
                                                                        {monthData.earlyCheckOuts.length}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ 
                                                                        color: '#6c757d',
                                                                        fontWeight: '500'
                                                                    }}>0</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        )}
                                    </CardBody>
                                </>
                            )}

                            {!isLoading && !reportData && (
                                <div className='alert m-3' style={{
                                    backgroundColor: '#f8f9fa',
                                    borderColor: '#dee2e6',
                                    color: '#6c757d',
                                    border: '1px solid #dee2e6'
                                }}>
                                    Select an employee and generate report to view leave details
                                </div>
                            )}
                        </div>
                    </div>
                </Card>
                {popup.show && (
                    <DetailsPopup
                        details={popup.details}
                        onClose={() => setPopup({ show: false, details: [], position: { x: 0, y: 0 }, title: '' })}
                        position={popup.position}
                        title={popup.title}
                    />
                )}
            </Page>
        </PageWrapper>
    );
};

export default EmployeeLeaveReport; 